/**
 * Patient Hooks
 *
 * TanStack Query hooks for patient data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreatePatientData,
  UpdatePatientData,
  PatientSearchFilters,
} from '@/domain/patient/types'
import { useAuth } from '@/application/auth'
import * as patientService from './patient.service'

// Query keys
export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (filters: PatientSearchFilters) => [...patientKeys.lists(), filters] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: string) => [...patientKeys.details(), id] as const,
  studies: (id: string) => [...patientKeys.detail(id), 'studies'] as const,
}

/**
 * Hook to fetch paginated patients list
 */
export function usePatients(filters?: PatientSearchFilters) {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: patientKeys.list(filters ?? {}),
    queryFn: () => patientService.getPatients(filters),
    enabled: isAuthenticated,
  })
}

/**
 * Hook to fetch a single patient
 */
export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: patientKeys.detail(id ?? ''),
    queryFn: () => patientService.getPatientById(id!),
    enabled: !!id,
  })
}

/**
 * Hook to search patients by document
 */
export function usePatientSearch(documentType: string, documentNumber: string) {
  return useQuery({
    queryKey: ['patients', 'search', documentType, documentNumber],
    queryFn: () => patientService.searchPatientsByDocument(documentType, documentNumber),
    enabled: !!documentType && !!documentNumber && documentNumber.length >= 3,
  })
}

/**
 * Hook to create a patient
 */
export function useCreatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePatientData) => patientService.createPatient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() })
    },
  })
}

/**
 * Hook to update a patient
 */
export function useUpdatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePatientData }) =>
      patientService.updatePatient(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() })
    },
  })
}

/**
 * Hook to delete a patient
 */
export function useDeletePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => patientService.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() })
    },
  })
}

/**
 * Hook to fetch patient's studies
 */
export function usePatientStudies(patientId: string | undefined) {
  return useQuery({
    queryKey: patientKeys.studies(patientId ?? ''),
    queryFn: () => patientService.getPatientStudies(patientId!),
    enabled: !!patientId,
  })
}
